import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Edit2, Trash2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { useAuth } from '@/hooks/useAuth';
import { captureError } from '@/lib/sentry';

interface Note {
  id: string;
  content: string;
  created_at: string;
}

interface ContactNotesProps {
  contactId: string;
}

export function ContactNotes({ contactId }: ContactNotesProps) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  const fetchNotes = async () => {
    try {
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotes(data || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
      captureError(error as Error, { feature: 'contact-detail-notes-fetch' });
      toast({
        title: "Error",
        description: "Failed to load notes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [contactId]);

  const addNote = async () => {
    if (!newNote.trim()) return;

    try {
      const { error } = await supabase
        .from('notes')
        .insert([{
          contact_id: contactId,
          content: newNote,
          user_id: user?.id,
        }]);

      if (error) throw error;

      setNewNote('');
      setEditingNote(null);
      await fetchNotes();
      
      toast({
        description: "Note added successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-notes-add-note' });
      toast({
        title: "Error",
        description: "Failed to add note",
        variant: "destructive",
      });
    }
  };

  const deleteNote = async (noteId: string) => {
    try {
      const { error } = await supabase
        .from('notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;

      setNotes(notes.filter(note => note.id !== noteId));
      
      toast({
        description: "Note deleted successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-notes-delete-note' });
      toast({
        title: "Error",
        description: "Failed to delete note",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading notes...</div>;
  }

  return (
    <div className="space-y-2">
      {/* Add Note Input */}
      <div 
        className="text-muted-foreground cursor-text rounded-lg border border-dashed p-2 hover:bg-accent/50 transition-colors"
        onClick={() => setEditingNote('new')}
      >
        {editingNote === 'new' ? (
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            onBlur={() => {
              if (newNote.trim()) {
                addNote();
              }
              setEditingNote(null);
            }}
            placeholder="Add a note..."
            className="w-full bg-transparent border-none focus:outline-none resize-none text-xs leading-relaxed min-h-[4rem]"
            autoFocus
          />
        ) : (
          <div className="text-xs">Add New Note</div>
        )}
      </div>

      {/* Existing Notes */}
      <div className="space-y-2">
        {notes.map((note) => (
          <div
            key={note.id}
            className="group relative rounded-lg bg-[#FFFBEB] dark:bg-amber-950/20 p-3"
          >
            <div className="text-[10px] text-muted-foreground mb-1">
              {format(new Date(note.created_at), 'PPp')}
            </div>
            {editingNote === note.id ? (
              <textarea
                value={note.content}
                onChange={(e) => {
                  const newContent = e.target.value;
                  setNotes(notes.map(n => 
                    n.id === note.id ? { ...n, content: newContent } : n
                  ));
                }}
                onBlur={async () => {
                  try {
                    const { error } = await supabase
                      .from('notes')
                      .update({ content: note.content })
                      .eq('id', note.id);

                    if (error) throw error;
                    setEditingNote(null);
                    
                    toast({
                      description: "Note updated successfully",
                    });
                  } catch (error) {
                    captureError(error as Error, { feature: 'contact-notes-update' });
                    toast({
                      title: "Error",
                      description: "Failed to update note",
                      variant: "destructive",
                    });
                  }
                }}
                className="w-full bg-transparent border-none focus:outline-none resize-none text-xs leading-relaxed min-h-[4rem]"
                autoFocus
              />
            ) : (
              <div className="relative min-h-[4rem]">
                <div
                  onClick={() => setEditingNote(note.id)}
                  className="cursor-text text-xs leading-relaxed whitespace-pre-wrap break-words"
                >
                  {note.content}
                </div>
                <div className="absolute top-0 right-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setEditingNote(note.id)}
                    className="h-6 w-6"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteNote(note.id)}
                    className="h-6 w-6 text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}